/**
 * Líneas de mercancía Invoice PTC: agrupación por configuración de producto (piezas/paquete)
 * y precio unitario USD/m³ desde la PI vinculada al packing list.
 */

function safe(s) {
  return s == null ? '' : String(s);
}

export function isRwNrPackingRow(row) {
  const txt = `${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`.toUpperCase();
  return /\b(RW\/NR|RW|WR)\b/.test(txt);
}

export function productNameFromPackingRow(row) {
  const t = safe(row?.productos_texto).trim();
  if (t) return t;
  const parts = [row?.grado_celda, row?.proveedor_celda, row?.almacen_celda]
    .map((x) => safe(x).trim())
    .filter(Boolean);
  return parts.join(' / ') || '—';
}

function fmtMeasureInvoice(v) {
  if (v == null || v === '') return '';
  const n = parseFloat(String(v).replace(/,/g, '.').replace(/\s/g, ''));
  if (Number.isFinite(n)) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }
  return safe(v).trim();
}

/** Línea de medidas (espesor x ancho x largo) como en packing list. */
export function dimensionLineFromPackingRow(row) {
  const espRaw = safe(row?.espesor).trim();
  const espPart = espRaw ? (/\bmm\b/i.test(espRaw) ? espRaw : `${espRaw} MM`) : '';
  const txt = `${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`.toUpperCase();
  const lar = row?.largo;
  if (/\bRW\b/.test(txt)) {
    const larFmt = lar != null && lar !== '' ? fmtMeasureInvoice(lar) : '';
    const parts = [espPart, 'RW', larFmt].filter(Boolean);
    return parts.join(' X ').replace(/\s+/g, ' ').trim();
  }
  const w = fmtMeasureInvoice(row?.ancho);
  const l = fmtMeasureInvoice(lar);
  if (!espPart && !w && !l) return '';
  return [espPart, w, l].filter(Boolean).join(' X ').replace(/\s+/g, ' ').trim();
}

function internalCodeFromGroup(g) {
  const nf = safe(g.sampleRow?.nf_celda).trim();
  if (nf) return nf;
  return g.piezasPorPaquete || '';
}

/** Valor mm para columnas Espessura / Largura / Comprimento del PDF PTB. */
export function formatDimMmForPtb(v) {
  const n = parseDimNumber(v);
  if (n == null) return '';
  if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return String(n);
}

/** Etiqueta corta PTB (ej. EUCALYPTUS VENEER → VENEER). */
export function etiquetaPtbFromDescription(productDescriptionSnapshot, productName) {
  const head = safe(productDescriptionSnapshot).trim() || safe(productName).trim();
  const upperHead = head.toUpperCase();
  if (!upperHead) return '';
  if (/\bVENEER\b/.test(upperHead)) return 'VENEER';
  const parts = upperHead.split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] || upperHead;
}

/**
 * Dimensiones PTB desde línea de packing list (espesor, ancho, largo).
 * No usar piezas_por_paquete: ahí va la configuración de paquetes (210, 1080…), no mm.
 */
export function ptbDimsFromPackingRow(row) {
  if (!row) return { espessura: '', largura: '', comprimento: '' };
  const txt = `${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`.toUpperCase();
  const esp = formatDimMmForPtb(row?.espesor);
  const lar = formatDimMmForPtb(row?.largo);
  if (/\bRW\b/.test(txt) || /\bRW\b/i.test(safe(row?.ancho))) {
    return { espessura: esp, largura: 'RW', comprimento: lar };
  }
  return {
    espessura: esp,
    largura: formatDimMmForPtb(row?.ancho),
    comprimento: lar,
  };
}

/** Bloque multilínea para columna MERCHANDISE DESCRIPTION del PDF. */
export function merchandiseDescriptionBlock(g, productDescriptionSnapshot) {
  const head =
    safe(productDescriptionSnapshot).trim() || g.productName || '—';
  const dim = dimensionLineFromPackingRow(g.sampleRow);
  const lines = [head];
  if (dim) lines.push(dim);
  if (g.sumBundles > 0) lines.push(`Bundles ${Math.round(g.sumBundles)}`);
  if (g.piezasPorPaquete) lines.push(`Pieces/Bundles ${g.piezasPorPaquete}`);
  if (g.sumPiezas > 0 && !isRwNrPackingRow(g.sampleRow)) {
    lines.push(`Pieces ${Math.round(g.sumPiezas)}`);
  }
  return lines.join('\n');
}

function normPiezas(piezas) {
  return safe(piezas).trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Clave de agrupación: configuración (piezas/paquete) + nombre de producto. */
export function productConfigKey(row) {
  return `${normPiezas(row?.piezas_por_paquete)}|${productNameFromPackingRow(row).toUpperCase()}`;
}

/** Número de medida: quita MM, espacios y comas de miles (1,220 → 1220). */
export function parseDimNumber(v) {
  let s = safe(v).replace(/\bmm\b/gi, '').trim();
  if (!s) return null;
  s = s.replace(/\s/g, '');
  while (/,(\d{3})(?=\d|,|$)/.test(s)) {
    s = s.replace(/,(\d{3})/g, '$1');
  }
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function isRwPackingRow(row) {
  return /\bRW\b/i.test(`${safe(row?.ancho)} ${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`);
}

/** Tripleta esp x ancho x largo (ancho puede ser 'rw') para cruce con real_size de la PI. */
export function dimensionTripleFromPackingRow(row) {
  const esp = parseDimNumber(row?.espesor);
  const lar = parseDimNumber(row?.largo);
  if (esp == null || lar == null) return null;
  if (isRwPackingRow(row)) return [esp, 'rw', lar];
  const anc = parseDimNumber(row?.ancho);
  if (anc == null) return null;
  return [esp, anc, lar];
}

/** Parsea real_size guardado en la PI (Contract No. → productos / usd_m3). */
export function dimensionTripleFromPiRealSize(realSize) {
  const raw = safe(realSize).trim();
  if (!raw) return null;
  const parts = raw.split(/\s+x\s+/i).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  if (parts.length === 2) {
    const a = parseDimNumber(parts[0]);
    const b = parseDimNumber(parts[1]);
    if (a != null && b != null) return [a, b];
    return null;
  }
  const esp = parseDimNumber(parts[0]);
  const mid = parts[1];
  const lar = parseDimNumber(parts[parts.length - 1]);
  if (esp == null || lar == null) return null;
  if (/^rw$/i.test(mid)) return [esp, 'rw', lar];
  const anc = parseDimNumber(mid);
  if (anc == null) return null;
  return [esp, anc, lar];
}

function triplesMatch(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (typeof x === 'string' || typeof y === 'string') {
      if (String(x).toLowerCase() !== String(y).toLowerCase()) return false;
    } else if (Math.abs(Number(x) - Number(y)) > 0.05) return false;
  }
  return true;
}

/** Clave texto para cruce secundario (PDF vs PI). */
export function packingLineRealSizeKey(row) {
  const dim = dimensionLineFromPackingRow(row);
  if (!dim) return '';
  return dim.replace(/\s+X\s+/gi, ' x ').replace(/\s+/g, ' ').trim();
}

function normRealSizeKey(s) {
  return safe(s)
    .toLowerCase()
    .replace(/\bmm\b/g, '')
    .replace(/(\d),(\d{3})/g, '$1$2')
    .replace(/\s+/g, ' ')
    .replace(/\s*x\s*/gi, ' x ')
    .trim();
}

function realSizeKeysForMatch(row) {
  const keys = new Set();
  const k = packingLineRealSizeKey(row);
  if (k) keys.add(normRealSizeKey(k));
  const triple = dimensionTripleFromPackingRow(row);
  if (triple) {
    keys.add(
      normRealSizeKey(
        triple.map((p) => (typeof p === 'number' ? String(p) : p)).join(' x ')
      )
    );
  }
  return [...keys];
}

/** PI u OC: mismos campos de precio (Contract No.). */
export function normalizePricingSource(src) {
  if (!src) return null;
  return {
    real_size: src.real_size,
    usd_m3: src.usd_m3,
    m3: src.m3,
    usd_total: src.usd_total,
    valor_proforma: src.valor_proforma ?? src.valor_ordencompra ?? null,
  };
}

function unitUsdM3AtProformaIndex(proforma, index) {
  const p = proforma?.usd_m3?.[index];
  if (p != null && Number.isFinite(Number(p)) && Number(p) > 0) {
    return Number(p);
  }
  const m3 = Number(proforma?.m3?.[index]);
  const total = Number(proforma?.usd_total?.[index]);
  if (m3 > 0 && total > 0) return round2(total / m3);
  return null;
}

function uniquePositiveUsdM3(proforma) {
  const priced = (proforma?.usd_m3 || [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
  return [...new Set(priced.map((n) => round2(n)))];
}

/** Promedio ponderado USD/m³ desde líneas de la PI (usd_m3, usd_total o valor total). */
export function usdM3AverageFromProforma(proforma) {
  const src = normalizePricingSource(proforma);
  if (!src) return null;
  const m3arr = src.m3 || [];
  let sumM3 = 0;
  let sumUsd = 0;
  for (let i = 0; i < m3arr.length; i += 1) {
    const m = Number(m3arr[i]);
    if (!(m > 0)) continue;
    sumM3 += m;
    const unit = unitUsdM3AtProformaIndex(src, i);
    if (unit != null) sumUsd += m * unit;
    else {
      const t = Number(src.usd_total?.[i]);
      if (t > 0) sumUsd += t;
    }
  }
  if (sumM3 > 0 && sumUsd > 0) return round2(sumUsd / sumM3);
  const valor = Number(src.valor_proforma);
  if (sumM3 > 0 && valor > 0) return round2(valor / sumM3);
  return null;
}

/** Precio unitario por defecto cuando todas las líneas comparten el mismo USD/m³ o un promedio único. */
export function usdM3DefaultFromProforma(proforma) {
  const src = normalizePricingSource(proforma);
  if (!src) return null;
  const unique = uniquePositiveUsdM3(src);
  if (unique.length === 1) return unique[0];
  return usdM3AverageFromProforma(src);
}

/**
 * Precio unitario USD/m³ de la PI (Contract No.) para una línea del packing list.
 * UNIT PRICE del PDF = este valor.
 */
export function usdM3FromProformaForLine(row, proforma) {
  const pi = normalizePricingSource(proforma);
  if (!pi?.real_size?.length) return null;
  const plTriple = dimensionTripleFromPackingRow(row);

  for (let i = 0; i < pi.real_size.length; i += 1) {
    const piTriple = dimensionTripleFromPiRealSize(pi.real_size[i]);
    if (plTriple && piTriple && triplesMatch(plTriple, piTriple)) {
      const p = unitUsdM3AtProformaIndex(pi, i);
      if (p != null) return p;
    }
  }

  const keys = realSizeKeysForMatch(row);
  for (let i = 0; i < pi.real_size.length; i += 1) {
    const piKey = normRealSizeKey(pi.real_size[i]);
    if (keys.some((k) => k === piKey)) {
      const p = unitUsdM3AtProformaIndex(pi, i);
      if (p != null) return p;
    }
  }
  for (let i = 0; i < pi.real_size.length; i += 1) {
    const piKey = normRealSizeKey(pi.real_size[i]);
    if (keys.some((k) => piKey.includes(k) || k.includes(piKey))) {
      const p = unitUsdM3AtProformaIndex(pi, i);
      if (p != null) return p;
    }
  }

  const fallback = usdM3DefaultFromProforma(pi);
  if (fallback != null) return fallback;
  return null;
}

export function round3(n) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  return Math.round(Number(n) * 1000) / 1000;
}

export function round2(n) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  return Math.round(Number(n) * 100) / 100;
}

const ONES = [
  '',
  'ONE',
  'TWO',
  'THREE',
  'FOUR',
  'FIVE',
  'SIX',
  'SEVEN',
  'EIGHT',
  'NINE',
  'TEN',
  'ELEVEN',
  'TWELVE',
  'THIRTEEN',
  'FOURTEEN',
  'FIFTEEN',
  'SIXTEEN',
  'SEVENTEEN',
  'EIGHTEEN',
  'NINETEEN',
];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

function numberToWordsEnUnder1000(n) {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const u = n % 10;
    return u ? `${TENS[t]} ${ONES[u]}` : TENS[t];
  }
  const h = Math.floor(n / 100);
  const r = n % 100;
  return r ? `${ONES[h]} HUNDRED ${numberToWordsEnUnder1000(r)}` : `${ONES[h]} HUNDRED`;
}

function numberToWordsEnInt(n) {
  if (n === 0) return 'ZERO';
  let s = '';
  const billions = Math.floor(n / 1e9);
  n %= 1e9;
  const millions = Math.floor(n / 1e6);
  n %= 1e6;
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  if (billions) s += `${numberToWordsEnUnder1000(billions)} BILLION `;
  if (millions) s += `${numberToWordsEnUnder1000(millions)} MILLION `;
  if (thousands) s += `${numberToWordsEnUnder1000(thousands)} THOUSAND `;
  if (rest) s += numberToWordsEnUnder1000(rest);
  return s.trim().replace(/\s+/g, ' ');
}

/** Monto USD en letras (inglés) para TOTAL DUE. */
export function usdAmountInWords(amount) {
  const n = round2(amount);
  if (!n) return 'ZERO DOLLARS';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  let s = `${numberToWordsEnInt(whole)} DOLLAR${whole === 1 ? '' : 'S'}`;
  if (cents) {
    s += ` AND ${numberToWordsEnInt(cents)} CENT${cents === 1 ? '' : 'S'}`;
  }
  return s;
}

/**
 * Agrupa líneas del packing list y calcula filas de mercancía para invoice/PDF.
 * @param {Array} lineas - detalle packing list (API o Excel)
 * @param {object|null} proforma - PI vinculada (última del PL)
 * @param {object} opts - { numeroInvoice, fechaInvoice, fechaEmbarque, currency }
 */
export function buildInvoicePtcMerchandise(lineas, proforma, opts = {}) {
  const pi = normalizePricingSource(proforma);
  const currency = safe(opts.currency || 'USD').trim().toUpperCase() || 'USD';
  const groups = new Map();

  for (const row of lineas || []) {
    const m3 = Number(row?.total_m3);
    if (!Number.isFinite(m3) || m3 <= 0) continue;

    const key = productConfigKey(row);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        piezasPorPaquete: normPiezas(row?.piezas_por_paquete),
        productName: productNameFromPackingRow(row),
        sumM3: 0,
        sumBundles: 0,
        sumPiezas: 0,
        unitPriceUsd: null,
        sampleRow: row,
      };
      groups.set(key, g);
    }
    g.sumM3 += m3;
    const bundles = Number(row?.cantidad_paquetes);
    if (Number.isFinite(bundles)) g.sumBundles += bundles;
    const pcs = Number(row?.total_piezas);
    if (Number.isFinite(pcs)) g.sumPiezas += pcs;

    const up = usdM3FromProformaForLine(row, pi);
    if (up != null) g.unitPriceUsd = up;
  }

  const defaultUsdM3 = usdM3DefaultFromProforma(pi);
  if (defaultUsdM3 != null) {
    for (const g of groups.values()) {
      if (g.unitPriceUsd == null) g.unitPriceUsd = defaultUsdM3;
    }
  }

  const lineasMercancia = [];
  let totalM3 = 0;
  let totalBundles = 0;
  let totalUsd = 0;

  for (const g of groups.values()) {
    const quantityM3 = round3(g.sumM3);
    // UNIT PRICE = usd_m3 de la PI (Contract No.); TOTAL VALUE = M³ × precio (2 dec.)
    const unitPriceUsd = g.unitPriceUsd != null ? round2(g.unitPriceUsd) : null;
    const totalValueUsd =
      unitPriceUsd != null && quantityM3 > 0
        ? round2(quantityM3 * unitPriceUsd)
        : null;

    totalM3 += quantityM3;
    totalBundles += g.sumBundles;
    if (totalValueUsd != null) totalUsd += totalValueUsd;

    const ptbDims = ptbDimsFromPackingRow(g.sampleRow);
    lineasMercancia.push({
      quantityM3,
      productName: g.productName,
      internalCode: internalCodeFromGroup(g),
      descriptionBlock: merchandiseDescriptionBlock(
        g,
        opts.productDescriptionSnapshot
      ),
      etiquetaPtb: etiquetaPtbFromDescription(
        opts.productDescriptionSnapshot,
        g.productName
      ),
      espessura: ptbDims.espessura,
      largura: ptbDims.largura,
      comprimento: ptbDims.comprimento,
      piezasPorPaquete: g.piezasPorPaquete,
      bundles: round3(g.sumBundles),
      totalPiezas: round3(g.sumPiezas),
      unitPriceUsd,
      totalValueUsd,
      currency,
      djcNum: safe(opts.numeroInvoice),
      djcDate: opts.fechaInvoice || opts.fechaEmbarque || '',
    });
  }

  lineasMercancia.sort((a, b) =>
    safe(a.piezasPorPaquete).localeCompare(safe(b.piezasPorPaquete), 'es')
  );

  return {
    lineasMercancia,
    totalM3: round3(totalM3),
    totalBundles: round3(totalBundles),
    totalUsd: round2(totalUsd),
    currency,
  };
}

/** Totales de paquetes y M³ sobre todas las líneas (incl. sin m3). */
export function packingListTotals(lineas) {
  let totalBundles = 0;
  let totalM3 = 0;
  for (const row of lineas || []) {
    const b = Number(row?.cantidad_paquetes);
    if (Number.isFinite(b)) totalBundles += b;
    const m = Number(row?.total_m3);
    if (Number.isFinite(m)) totalM3 += m;
  }
  return { totalBundles: round3(totalBundles), totalM3: round3(totalM3) };
}
